/**
 * All Categories Tab
 *
 * Shows all categories in a unified list with ability to:
 * - Toggle between showing all categories or only inactive ones
 * - Enable/disable categories
 * - Edit category settings
 *
 * This tab provides a complete overview of all categories regardless of their
 * visibility settings on home, carousel, or shop pages.
 *
 * @module HomeLayoutManager/components/AllCategoriesTab
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  IconButton,
  Chip,
  Tooltip,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
} from '@mui/material';
import {
  Category as CategoryIcon,
  Edit as EditIcon,
  Visibility as VisibleIcon,
  VisibilityOff as HiddenIcon,
  Home as HomeIcon,
  ViewCarousel as CarouselIcon,
  Storefront as ShopIcon,
  FilterList as FilterListIcon,
  LiquorOutlined as LiquorIcon,
  RemoveRedEye as ViewProductsIcon,
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import authInstance from '../../../services/httpClient';
import type { CategoryDraft } from '../types';
import CategoryFormDialog, { type CategoryDraftChanges } from '../../../components/CategoryManagement/CategoryFormDialog';

interface AllCategoriesTabProps {
  /** All categories from draft */
  categories: CategoryDraft[];
  /** Callback when a category is updated */
  onCategoryUpdate: (categoryId: string, updates: Partial<CategoryDraft>) => void;
  /** Callback to view products in a category */
  onViewProducts: (categoryId: string, categoryName: string) => void;
  /** Whether the panel is currently saving */
  isSaving?: boolean;
  /**
   * Re-read the draft after a category is created or deleted. Those two write the live
   * category table straight away (a category needs a row before anything can point at it),
   * and the draft syncs new and removed categories from it.
   */
  onCategoriesChanged?: () => void | Promise<void>;
}

/**
 * All Categories Tab Component
 *
 * Lists every category; the toggle (on by default) narrows it to the active ones.
 */
export default function AllCategoriesTab({
  categories,
  onCategoryUpdate,
  onViewProducts,
  isSaving = false,
  onCategoriesChanged,
}: AllCategoriesTabProps): React.ReactElement {
  // Active categories by default (that is the live navigation); switch the toggle off to
  // see everything, inactive included (Jason, 23 Oct 2026).
  const [showOnlyActive, setShowOnlyActive] = useState(true);

  // Edit dialog state
  const [editingCategory, setEditingCategory] = useState<CategoryDraft | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // Create / delete (live writes, see onCategoriesChanged)
  const [createOpen, setCreateOpen] = useState(false);
  const [deleting, setDeleting] = useState<CategoryDraft | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleCreated = useCallback(async () => {
    setCreateOpen(false);
    await onCategoriesChanged?.();
  }, [onCategoriesChanged]);

  const confirmDelete = useCallback(async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await authInstance.delete(`/admin/categories/${deleting.id}`);
      setDeleting(null);
      await onCategoriesChanged?.();
    } catch (err: any) {
      // e.g. "has 12 associated products": reassign them first, or deactivate instead
      setDeleteError(err?.response?.data?.message || err?.message || 'Could not delete this category');
    } finally {
      setDeleteBusy(false);
    }
  }, [deleting, onCategoriesChanged]);

  // Filter categories based on toggle
  const displayedCategories = useMemo(() => {
    const filtered = showOnlyActive
      ? categories.filter((cat) => cat.isActive)
      : categories;
    // Sort by name for easier finding
    return [...filtered].sort((a, b) => a.name.localeCompare(b.name));
  }, [categories, showOnlyActive]);

  // Count stats
  const activeCount = categories.filter((cat) => cat.isActive).length;
  const inactiveCount = categories.filter((cat) => !cat.isActive).length;

  // Toggle category active state
  const handleToggleActive = useCallback(
    (category: CategoryDraft) => {
      onCategoryUpdate(category.id, { isActive: !category.isActive });
    },
    [onCategoryUpdate]
  );

  // Open edit dialog
  const handleEditClick = useCallback((category: CategoryDraft) => {
    setEditingCategory({ ...category });
    setEditDialogOpen(true);
  }, []);

  // Close edit dialog
  const handleCloseEditDialog = useCallback(() => {
    setEditDialogOpen(false);
    setEditingCategory(null);
  }, []);

  // Merge the editor's fields into the draft. Nothing is written to the live
  // category here — Publish Live does that, for these fields along with
  // everything else already in the draft.
  const handleDraftSave = useCallback(
    (changes: CategoryDraftChanges) => {
      if (!editingCategory) return;
      onCategoryUpdate(editingCategory.id, changes);
      handleCloseEditDialog();
    },
    [editingCategory, onCategoryUpdate, handleCloseEditDialog]
  );

  return (
    <Box>
      {/* Header with toggle */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          p: 1.5,
          borderRadius: 2,
          background: 'linear-gradient(135deg, #FF002C 0%, #CC0023 100%)',
          boxShadow: '0 4px 12px rgba(255, 0, 44, 0.25)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CategoryIcon sx={{ color: 'white', fontSize: 20 }} />
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              letterSpacing: '0.05em',
              color: 'white',
              textTransform: 'uppercase',
              fontSize: '0.75rem',
            }}
          >
            All Categories ({categories.length})
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={`${activeCount} active`}
            size="small"
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontSize: '0.7rem' }}
          />
          <Chip
            label={`${inactiveCount} inactive`}
            size="small"
            sx={{ bgcolor: 'rgba(0,0,0,0.2)', color: 'white', fontSize: '0.7rem' }}
          />
        </Box>
      </Box>

      {/* Toggle filter */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          p: 1,
          bgcolor: 'background.default',
          borderRadius: 1,
        }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={showOnlyActive}
              onChange={(e) => setShowOnlyActive(e.target.checked)}
              size="small"
            />
          }
          label={
            <Typography variant="body2" color="text.secondary">
              Show only active categories
            </Typography>
          }
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            Showing {displayedCategories.length} of {categories.length}
          </Typography>
          <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)} disabled={isSaving}>
            New category
          </Button>
        </Box>
      </Box>

      {/* Categories list */}
      {displayedCategories.length === 0 ? (
        <Box
          sx={{
            py: 6,
            textAlign: 'center',
            color: 'text.secondary',
          }}
        >
          <CategoryIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
          <Typography variant="body2">
            {showOnlyActive
              ? 'No active categories'
              : 'No categories found'}
          </Typography>
          {showOnlyActive && activeCount === 0 && inactiveCount > 0 && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
              Every category is inactive. Switch the toggle off to see them.
            </Typography>
          )}
        </Box>
      ) : (
        <List sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
          {displayedCategories.map((category, index) => (
            <React.Fragment key={category.id}>
              {index > 0 && <Divider />}
              <ListItem
                sx={{
                  opacity: category.isActive ? 1 : 0.6,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
                secondaryAction={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* Visibility indicators */}
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title={category.showOnHomePage ? 'Shown on Home' : 'Hidden from Home'}>
                        <HomeIcon
                          sx={{
                            fontSize: 16,
                            color: category.showOnHomePage ? 'primary.main' : 'action.disabled',
                          }}
                        />
                      </Tooltip>
                      <Tooltip title={category.showInCarousel ? 'Shown in Carousel' : 'Hidden from Carousel'}>
                        <CarouselIcon
                          sx={{
                            fontSize: 16,
                            color: category.showInCarousel ? 'primary.main' : 'action.disabled',
                          }}
                        />
                      </Tooltip>
                      <Tooltip title={category.showInShop ? 'Shown in Shop' : 'Hidden from Shop'}>
                        <ShopIcon
                          sx={{
                            fontSize: 16,
                            color: category.showInShop ? 'primary.main' : 'action.disabled',
                          }}
                        />
                      </Tooltip>
                      {category.showTagFilters && (
                        <Tooltip title="Shows tag filter chips">
                          <FilterListIcon
                            sx={{
                              fontSize: 16,
                              color: 'secondary.main',
                            }}
                          />
                        </Tooltip>
                      )}
                      {category.respectsEarlyCutoff && (
                        <Tooltip title="Respects early cutoff time">
                          <LiquorIcon
                            sx={{
                              fontSize: 16,
                              color: 'warning.main',
                            }}
                          />
                        </Tooltip>
                      )}
                    </Box>

                    {/* View products button */}
                    <Tooltip title="View products">
                      <IconButton
                        size="small"
                        onClick={() => onViewProducts(category.id, category.name)}
                        disabled={isSaving}
                      >
                        <ViewProductsIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    {/* Edit button */}
                    <Tooltip title="Edit category">
                      <IconButton
                        size="small"
                        onClick={() => handleEditClick(category)}
                        disabled={isSaving}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    {/* Delete (refused by the API while products still use the category) */}
                    <Tooltip title="Delete category">
                      <IconButton
                        size="small"
                        onClick={() => { setDeleteError(null); setDeleting(category); }}
                        disabled={isSaving}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    {/* Active toggle */}
                    <Tooltip title={category.isActive ? 'Deactivate category' : 'Activate category'}>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleActive(category)}
                        disabled={isSaving}
                        sx={{
                          color: category.isActive ? 'success.main' : 'error.main',
                        }}
                      >
                        {category.isActive ? <VisibleIcon /> : <HiddenIcon />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                }
              >
                <ListItemAvatar>
                  <Avatar
                    src={category.imageUrl || undefined}
                    sx={{
                      bgcolor: '#FF002C',
                      width: 40,
                      height: 40,
                    }}
                  >
                    {category.imageUrl ? null : <CategoryIcon />}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight={500}>
                        {category.name}
                      </Typography>
                      {!category.isActive && (
                        <Chip
                          label="Inactive"
                          size="small"
                          color="default"
                          sx={{ fontSize: '0.65rem', height: 18 }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {category.homeFilterType === 'category'
                        ? 'Shows category products'
                        : category.homeFilterType === 'tag'
                        ? `Tag filter: ${category.homeFilterValue || 'none'}`
                        : category.homeFilterType === 'newOrPopular'
                        ? 'New & Popular products'
                        : category.homeFilterType === 'none'
                        ? 'Icon only (no products)'
                        : category.homeFilterType}
                    </Typography>
                  }
                />
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      )}

      {/* Edit dialog.
          This is the ONLY category editor in the app. It used to be a small
          inline form here, with a second, richer editor (CategoryFormDialog)
          reachable from the Home/Carousel/Shop tabs — which wrote LIVE, skipping
          the draft workflow it sat inside. The rich dialog is now used here in
          draftMode, so there is one editor, one field set, and one write path:
          the draft, published by Publish Live. */}
      {/* New category: name + icon (+ display locations), written live, then the draft re-reads it */}
      {createOpen && (
        <CategoryFormDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          category={null}
          onSave={handleCreated}
        />
      )}

      <Dialog open={Boolean(deleting)} onClose={() => (deleteBusy ? null : setDeleting(null))} maxWidth="xs" fullWidth>
        <DialogTitle>Delete {deleting?.name}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This removes the category straight away, not on Publish Live. A category that still has products
            cannot be deleted: move them to another category first, or deactivate it instead.
          </DialogContentText>
          {deleteError && <Alert severity="error" sx={{ mt: 2 }}>{deleteError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)} disabled={deleteBusy}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDelete} disabled={deleteBusy}>
            {deleteBusy ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {editingCategory && (
        <CategoryFormDialog
          open={editDialogOpen}
          onClose={handleCloseEditDialog}
          category={editingCategory}
          draftMode
          onDraftSave={handleDraftSave}
          onSave={handleCloseEditDialog}
        />
      )}
    </Box>
  );
}
